import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// Assuming Header component is in this path, though not directly used in the PDF generation logic itself

function BIDP({ locationData, isSearchApplied }) { // Accept props
  // Mapping for Lupon/Komite text based on report category
  const luponTextMap = {
    'Sectoral Plan for Agriculture and Environment': 'Agrikultura at Pangkabuhayan',
    'Sectoral Plan for Environmental Protection and Management': 'Pangangalaga at Pangangasiwa ng Kapaliparan',
    'Sectoral Plan for Health': 'Kalusugan at Nutrisyon',
    'Sectoral Plan for Nutrition': 'Kalusugan at Nutrisyon',
    'Sectoral Plan for Infrastructure': 'Pagawain',
    'Sectoral Plan for Social Services': 'Serbisyong Panlipunan', // Added for Social Services
    'Sectoral Plan for Youth and Sports Development': 'Pangkabataan at Pampalakasang Pagpapaunlad',
  };

  // Mapping for API 'type' parameter based on report category
  const reportTypeToApiTypeMap = {
    'Sectoral Plan for Agriculture and Environment': 1,
    'Sectoral Plan for Environmental Protection and Management': 2,
    'Sectoral Plan for Health': 3,
    'Sectoral Plan for Nutrition': 4,
    'Sectoral Plan for Infrastructure': 5,
    'Sectoral Plan for Social Services': 6, // Added for Social Services
    'Sectoral Plan for Youth and Sports Development': 7,
  };


  const generateReport = async (reportType, reportLocationData, externalData = []) => { // Made async and added externalData parameter
    const { provinceName, municipalName, barangayName} = reportLocationData;
    const doc = new jsPDF("landscape", "pt", "A4"); // Use landscape for wider tables

    // Add text 'BIDANI_BIDP Form 2012' with 1 inch left margin and 0.75 inch top margin
    doc.setFontSize(10); // You can adjust font size if needed
    doc.text('BIDANI_BIDP Form 2012', 40, 54); // 40pt left margin, 54pt top margin

    // Add 'Republic of the Philippines' centered and in bold
    doc.setFontSize(12); // Slightly larger font for the title
    doc.setFont(undefined, 'bold');
    doc.text('Republic of the Philippines', doc.internal.pageSize.width / 2, 60, { align: 'center' });
    doc.setFont(undefined, 'normal'); // Reset font to normal

    // Add 'Barangay: ${barangayName}, ${municipalName}, ${provinceName}' centered
    // Using hardcoded values for municipalName and provinceName for now, as they are not yet passed
    doc.setFontSize(10);
    const prefixText = 'Barangay: ';
    const valueText = `${barangayName}, ${municipalName}, ${provinceName}`; 
    const locationText = prefixText + valueText;
    const locationTextY = 75;
    doc.text(locationText, doc.internal.pageSize.width / 2, locationTextY, { align: 'center' });

    // Add underline starting from the value after "Barangay: "
    const prefixWidth = doc.getStringUnitWidth(prefixText) * doc.internal.getFontSize();
    const totalTextWidth = doc.getStringUnitWidth(locationText) * doc.internal.getFontSize();
    const textStartX = (doc.internal.pageSize.width / 2) - (totalTextWidth / 2);
    const underlineStartX = textStartX + prefixWidth;
    const underlineEndX = textStartX + totalTextWidth;
    doc.line(underlineStartX, locationTextY + 3, underlineEndX, locationTextY + 3); // Underline 3 points below text

    // Add 'Plano ng Gawain para sa __________________' centered
    doc.setFontSize(10); // Maintain font size or adjust as needed
    const planoTextY = 90;
    doc.text('Plano ng Gawain para sa __________________', doc.internal.pageSize.width / 2, planoTextY, { align: 'center' });

    // Get the correct luponText based on the reportType
    const currentLuponText = luponTextMap[reportType] || 'N/A'; // Fallback if not found
    
    // Add 'Lupon/Komite: [Dynamic Lupon Text]' left-aligned and underlined
    doc.setFontSize(10);
    const luponText = `Lupon/Komite: ${currentLuponText}`;
    const luponTextX = 40; // 1 inch left margin
    const luponTextY = 105;
    doc.text(luponText, luponTextX, luponTextY, { align: 'left' });

    // Underline 'Lupon/Komite: Agrikultura at Pangkabuhayan'
    const luponTextWidth = doc.getStringUnitWidth(luponText) * doc.internal.getFontSize();
    doc.line(luponTextX, luponTextY + 3, luponTextX + luponTextWidth, luponTextY + 3); // Underline 3 points below text

    const currentDate = new Date().toLocaleDateString('en-GB'); // DD/MM/YYYY format

    // --- Fetch data for specific report types dynamically ---
    let fetchedReportData = {};
    const apiType = reportTypeToApiTypeMap[reportType];
    let baseUrl = `${process.env.REACT_APP_API_URL}/api/locations/api/reports/bidp`
    if (apiType) { // Only attempt to fetch if a valid API type is mapped
      try {
        // Construct the API URL with dynamic barangay and dynamic type
        const location = `${provinceName}_${municipalName}_${barangayName}`
        const apiUrl = `${baseUrl}?location=${encodeURIComponent(location)}&type=${apiType}`;
        const response = await fetch(apiUrl, {
          method: 'GET', 
          headers: {
            'x-api-key': process.env.REACT_APP_API_KEY
          }
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, Response: ${errorText}`);
        }
        const data = await response.json();
        // Access the first element of the array, as per the provided API response structure
        if (Array.isArray(data) && data.length > 0) {
          fetchedReportData = data[0];
        } else {
          console.warn(`API for ${reportType} returned an empty array or non-array data. Data received:`, data);
          fetchedReportData = {
            poverty_threshold: '0',
            food_threshold: '0',
            no_income_count: '0',
            hayop_bakuna: '0',
            baboy_disposal: '0',
            farmer_count: '0',
            environmental_water: '0',
            environmental_toilet: '0',
            environmental_danger_zone: '0',
            environmental_waste_disposal: '0',
            health_child_mortality: '0',
            health_maternal_mortality: '0',
            health_large_families: '0',
            health_family_planning_knowledge: '0',
            health_diseases: '0',
            health_unvaccinated_children: '0',
            health_incomplete_vaccination: '0',
            nutrition_low_weight_children: '0',
            nutrition_no_toilet: '0',
            nutrition_incomplete_vaccine: '0',
            nutrition_no_vaccine: '0',
            nutrition_not_dewormed: '0',
            nutrition_no_vegetable_garden: '0',
            nutrition_large_family: '0',
            nutrition_no_clean_water: '0',
            nutrition_improper_waste_disposal: '0',
            nutrition_insufficient_income: '0',
            nutrition_insufficient_food: '0',
            infrastructure_no_electricity: '0',
            social_no_house_land: '0',
            social_weak_house_structure: '0',
            social_disabled_out_of_school_6_12: '0',
            social_disabled_out_of_school_13_16: '0',
            social_not_registered_municipal: '0',
            social_not_voter: '0',
            social_unmarried_couples: '0',
            social_separated_solo_parent: '0',
            youth_no_elementary: '0',
            youth_no_highschool: '0',
          };
        }
      } catch (error) {
        console.error(`Error fetching data for ${reportType}:`, error);
        // Set default/fallback values in case of fetch or JSON parsing error
        fetchedReportData = {
          poverty_threshold: '0',
          food_threshold: '0',
          no_income_count: '0',
          hayop_bakuna: '0',
          baboy_disposal: '0',
          farmer_count: '0',
          environmental_water: '0',
          environmental_toilet: '0',
          environmental_danger_zone: '0',
          environmental_waste_disposal: '0',
          health_child_mortality: '0',
          health_maternal_mortality: '0',
          health_large_families: '0',
          health_family_planning_knowledge: '0',
          health_diseases: '0',
          health_unvaccinated_children: '0',
          health_incomplete_vaccination: '0',
          nutrition_low_weight_children: '0',
          nutrition_no_toilet: '0',
          nutrition_incomplete_vaccine: '0',
          nutrition_no_vaccine: '0',
          nutrition_not_dewormed: '0',
          nutrition_no_vegetable_garden: '0',
          nutrition_large_family: '0',
          nutrition_no_clean_water: '0',
          nutrition_improper_waste_disposal: '0',
          nutrition_insufficient_income: '0',
          nutrition_insufficient_food: '0',
          infrastructure_no_electricity: '0',
          social_no_house_land: '0',
          social_weak_house_structure: '0',
          social_disabled_out_of_school_6_12: '0',
          social_disabled_out_of_school_13_16: '0',
          social_not_registered_municipal: '0',
          social_not_voter: '0',
          social_unmarried_couples: '0',
          social_separated_solo_parent: '0',
          youth_no_elementary: '0',
          youth_no_highschool: '0',
        };
      }
    } else {
      // If no API type is mapped, set all to 0
      fetchedReportData = {
        poverty_threshold: '0',
        food_threshold: '0',
        no_income_count: '0',
        hayop_bakuna: '0',
        baboy_disposal: '0',
        farmer_count: '0',
        environmental_water: '0',
        environmental_toilet: '0',
        environmental_danger_zone: '0',
        environmental_waste_disposal: '0',
        health_child_mortality: '0',
        health_maternal_mortality: '0',
        health_large_families: '0',
        health_family_planning_knowledge: '0',
        health_diseases: '0',
        health_unvaccinated_children: '0',
        health_incomplete_vaccination: '0',
        uw: '0',
        NA: '0',
        NB: '0',
        NC: '0',
        ND: '0',
        NE: '0',
        NF: '0',
        NG: '0',
        NH: '0',
        NI: '0',
        NJ: '0',
        infrastructure_no_electricity: '0',
        informal_settlers: '0',
        mahina_yari: '0',
        disabled_6_12: '0',
        disabled_13_16: '0',
        unregistered_registrar: '0',
        unregistered_comelec: '0',
        not_married: '0',
        solo_parent: '0',
        elem: '0',
        hs: '0',
      };
    }


    // --- AutoTable Configuration ---
    const head = [
      [
        { content: "Suliranin/\nPangangailangan", rowSpan: 2, styles: { valign: 'middle' } },
        { content: "Proyekto/\nSolusyon", rowSpan: 2, styles: { valign: 'middle' } },
        { content: "Mga Takdang Gawain", rowSpan: 2, styles: { valign: 'middle' } },
        { content: "Kailan Isasagawa\n(Buwan at Taon)", rowSpan: 2, styles: { valign: 'middle' } },
        { content: `Kabuuang Bilang/Target\n as of\n${currentDate}`, rowSpan: 2, styles: { valign: 'middle', halign: 'center' } }, // MODIFIED HEADER HERE
        { content: "Bilang/Target", colSpan: 3, styles: { halign: 'center' } },
        { content: "Pondo at iba pang\nPangangailangan", colSpan: 4, styles: { halign: 'center' } },
      ],
      [
        { content: "Unang Taon", styles: { halign: 'center' } },
        { content: "Ikalawang Taon", styles: { halign: 'center' } },
        { content: "Ikatlong Taon", styles: { halign: 'center' } },
        { content: "Kabahagi ng Barangay\n(Uri/Bilang/Halaga)", styles: { halign: 'center' } },
        { content: "Pagmumulan", styles: { halign: 'center' } },
        { content: "Kabahagi ng Ahensya o iba pang\nTanggapan(Uri/Bilang/Halaga)", styles: { halign: 'center' } },
        { content: "Pagmumulan", styles: { halign: 'center' } },
      ]
    ];

    let dummyBodyData = [];

    if (reportType === 'Sectoral Plan for Agriculture and Environment') {
      dummyBodyData = [
        ["Mga Pamilya na hindi sapat ang pinagkakakitaan (Below Poverty Threshold)", "", "", "", fetchedReportData.poverty_threshold || '0', "", "", "", "", "", "", ""],
        ["Kakulangan ng pagkain dulot ng hindi sapat na pinagkakakitaan ng pamilya (Below Food Threshold)", "", "", "", fetchedReportData.food_threshold || '0', "", "", "", "", "", "", ""],
        ["Mga miyembro ng pamilya na walang hanapbuhay (edad 18 - 64 taon)", "", "", "", fetchedReportData.no_income_count || '0', "", "", "", "", "", "", ""],
        ["Bilang ng hayop na maaaring mangailangan ng bakuna \nBaka\nKalabaw\nAso", "", "", "", (fetchedReportData.hayop_bakuna ? '\n\n' + fetchedReportData.hayop_bakuna : '0'), "", "", "", "", "", "", ""],
        ["Pagsubaybay sa mga nagaalaga ng baboy upang maiwasan ang maling pagtapon ng dumi", "", "", "", fetchedReportData.baboy_disposal || '0', "", "", "", "", "", "", ""],
        ["Pagbibigay ng karagdagang kaalaman sa mga magsasaka tungkol sa makabagong pagsasaka", "", "", "", fetchedReportData.farmer_count || '0', "", "", "", "", "", "", ""],
      ];
    } else if (reportType === 'Sectoral Plan for Environmental Protection and Management') {
      dummyBodyData = [
        ["Mga pamilya na walang maayos na pinagkukunan ng malinis na tubig", "", "", "", (fetchedReportData.environmental_water || '0') + '\npamilya', "", "", "", "", "", "", ""],
        ["Mga pamilya na walang maayos ng palikuran", "", "", "", (fetchedReportData.environmental_toilet || '0') + '\npamilya', "", "", "", "", "", "", ""],
        ["Mga bahay na nasa delikadong lugar", "", "", "", (fetchedReportData.environmental_danger_zone || '0') + '\npamilya', "", "", "", "", "", "", ""],
        ["Mga pamilyang walang maayos na pagtatapon ng basura", "", "", "", (fetchedReportData.environmental_waste_disposal || '0') + '\npamilya', "", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", "", "", "", "", ""], // Empty row 1
        ["", "", "", "", "", "", "", "", "", "", "", ""], // Empty row 2
      ];
    } else if (reportType === 'Sectoral Plan for Health') {
      dummyBodyData = [
        ["Mga batang edad 0-5 taon na namatay", "", "", "", (fetchedReportData.health_child_mortality || '0') + ' tao', "", "", "", "", "", "", ""],
        ["Mga buntis na namatay", "", "", "", (fetchedReportData.health_maternal_mortality || '0') + ' tao', "", "", "", "", "", "", ""],
        ["Mga pamilya na higit sa 5 miyembro", "", "", "", (fetchedReportData.health_large_families || '0') + ' pamilya', "", "", "", "", "", "", ""],
        ["Kakulangan sa kaalaman sa tamang pagpaplano ng pamilya", "", "", "", (fetchedReportData.health_family_planning_knowledge || '0') + ' pamilya', "", "", "", "", "", "", ""],
        ["Pagkakaroon ng mga sakit: \n\tHeart Disease\n\tCancer\n\tTubercolosis\n\tRenal Disease\n\tDiabetes Mellitus\n\tAsthma", "", "", "", (fetchedReportData.health_diseases ? '\n' + fetchedReportData.health_diseases : '0'), "", "", "", "", "", "", ""], // Removed 2 \n
        ["Mga batang walang bakuna (0-11 buwan)", "", "", "", (fetchedReportData.health_unvaccinated_children || '0') + ' bata', "", "", "", "", "", "", ""],
        ["Mga batang di kompleto ang bakuna (0-11 buwan)", "", "", "", (fetchedReportData.health_incomplete_vaccination || '0') + ' bata', "", "", "", "", "", "", ""],
      ];
    } else if (reportType === 'Sectoral Plan for Nutrition') {
      dummyBodyData = [
        ["Mga batang edad 0-6 taon na mababa ang timbang", "", "", "", (fetchedReportData.uw || '0') + ' bata', "", "", "", "", "", "", ""],
        ["Walang maayos na palikuran", "", "", "", (fetchedReportData.NA || '0') + ' bata', "", "", "", "", "", "", ""],
        ["Hindi kumpleto ang bakuna", "", "", "", (fetchedReportData.NB || '0') + ' bata', "", "", "", "", "", "", ""],
        ["Walang bakuna", "", "", "", (fetchedReportData.NC || '0') + ' bata', "", "", "", "", "", "", ""],
        ["Hindi napurga", "", "", "", (fetchedReportData.ND || '0') + ' bata', "", "", "", "", "", "", ""],
        ["Walang gulayan", "", "", "", (fetchedReportData.NE || '0') + ' bata', "", "", "", "", "", "", ""],
        ["Malaki ang pamilya (>5)", "", "", "", (fetchedReportData.NF || '0') + ' bata', "", "", "", "", "", "", ""],
        ["Walang maayos na pinagkukunan ng tubig", "", "", "", (fetchedReportData.NG || '0') + ' bata', "", "", "", "", "", "", ""],
        ["Hindi maayos na pagtatapon ng basura", "", "", "", (fetchedReportData.NH || '0') + ' bata', "", "", "", "", "", "", ""],
        ["Hindi sapat ang pinagkakakitaan ng pamilya", "", "", "", (fetchedReportData.NI || '0') + ' bata', "", "", "", "", "", "", ""],
        ["Kulang ang pagkain dulot ng hindi sapat ang pinagkakakitaan ng pamilya", "", "", "", (fetchedReportData.NJ || '0') + ' bata', "", "", "", "", "", "", ""],
      ];
    } else if (reportType === 'Sectoral Plan for Infrastructure') {
      dummyBodyData = [
        ["Mga pamilya na walang kuryente", "", "", "", (fetchedReportData.infrastructure_no_electricity || '0') + ' bahay', "", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", "", "", "", "", ""], // Blank row 1
        ["", "", "", "", "", "", "", "", "", "", "", ""], // Blank row 2
        ["", "", "", "", "", "", "", "", "", "", "", ""], // Blank row 3
        ["", "", "", "", "", "", "", "", "", "", "", ""], // Blank row 4
        ["", "", "", "", "", "", "", "", "", "", "", ""], // Blank row 5
      ];
    } else if (reportType === 'Sectoral Plan for Social Services') {
      dummyBodyData = [
        ["Mga pamilyang walang sariling bahay at lupa (informal settlers)", "", "", "", (fetchedReportData.informal_settlers || '0') + ' pamilya', "", "", "", "", "", "", ""],
        ["Mga pamilya na walang matibay na yari ng bahay", "", "", "", (fetchedReportData.mahina_yari || '0') + ' pamilya', "", "", "", "", "", "", ""],
        ["Mga batang may kapansanan na hindi nag-aaral (6-12 taon gulang)", "", "", "", (fetchedReportData.disabled_6_12 || '0') + ' bata', "", "", "", "", "", "", ""],
        ["Mga batang may kapansanan na hindi nag-aaral (13-16 taon gulang)", "", "", "", (fetchedReportData.disabled_13_16 || '0') + ' bata', "", "", "", "", "", "", ""],
        ["Mga miyembro ng pamilya na hindi nakatala sa 'Municipal Registrar'", "", "", "", (fetchedReportData.unregistered_registrar || '0') + ' tao', "", "", "", "", "", "", ""],
        ["Mga miyembro ng pamilya na hindi botante (COMELEC) ng barangay (18 taon pataas)", "", "", "", (fetchedReportData.unregistered_comelec || '0') + ' tao', "", "", "", "", "", "", ""],
        ["Mga di kasal na mag-asawa na maaaring nagnanais na magpakasal", "", "", "", (fetchedReportData.not_married || '0') + ' mag-asawa', "", "", "", "", "", "", ""],
        ["Mga hiwalay na mag asawa o solo parent na maaaring mag benepisyo sa 'Solo Parent Act'", "", "", "", (fetchedReportData.solo_parent || '0') + ' tao', "", "", "", "", "", "", ""],
      ];
    } else if (reportType === 'Sectoral Plan for Youth and Sports Development') {
      dummyBodyData = [
        ["Mga kabataang hindi nagaaral ng elementarya", "", "", "", (fetchedReportData.elem || '0') + ' bata', "", "", "", "", "", "", ""],
        ["Mga kabatang hindi nagaaral ng high school", "", "", "", (fetchedReportData.hs || '0') + ' bata', "", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", "", "", "", "", ""], // Blank row 1
        ["", "", "", "", "", "", "", "", "", "", "", ""], // Blank row 2
        ["", "", "", "", "", "", "", "", "", "", "", ""], // Blank row 3
      ];
    }
    // Add more else if blocks for other report types as needed


    // Add any additional rows from externalData if provided
    if (externalData.length > 0) {
      dummyBodyData = dummyBodyData.concat(externalData.map(row => [
        row.suliranin,
        row.proyekto,
        row.gawain,
        row.kailan_isasagawa,
        row.kabuuang_target,
        row.target_unang_taon,
        row.target_ikalawang_taon,
        row.target_ikatlong_taon,
        row.barangay_kabahagi,
        row.barangay_pagmumulan,
        row.ahensya_kabahagi,
        row.ahensya_pagmumulan
      ]));
    }


    // Call autoTable. It modifies the doc object.
    autoTable(doc, {
      startY: 110, // Adjusted start position to accommodate the new text
      head: head,
      body: dummyBodyData, // Use dummyBodyData or your fetched reportData
      theme: 'grid', // 'striped', 'grid', 'plain'
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [255, 255, 255], // White background
        textColor: [0, 0, 0],     // Black text
        halign: 'center',
        valign: 'middle',
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fontSize: 8,
      },
      columnStyles: {
        0: { halign: 'left' },  // Suliranin/Pangangailangan
        1: { cellWidth: 80, halign: 'left' },  // Proyekto/Solusyon
        2: { cellWidth: 80, halign: 'left' }, // Mga Takdang Gawain
        3: { cellWidth: 60, halign: 'center' },// Kailan Isasagawa (Buwan at Taon)
        4: { cellWidth: 65, halign: 'center' },// Kabuuang Bilang/Target (NEW)
        5: { cellWidth: 45, halign: 'center' },// Bilang/Target - Unang Taon
        6: { cellWidth: 45, halign: 'center' },// Bilang/Target - Ikalawang Taon
        7: { cellWidth: 45, halign: 'center' },// Bilang/Target - Ikatlong Taon
        8: { cellWidth: 60, halign: 'left' },  // Pondo - Kabahagi ng Barangay
        9: { cellWidth: 55, halign: 'center' },// Pondo - Pagmumulan (Barangay)
        10: { cellWidth: 60, halign: 'left' }, // Pondo - Kabahagi of Ahensya
        11: { cellWidth: 55, halign: 'center' },// Pondo - Pagmumulan (Ahensya)
      },
      didParseCell: function (data) {
        if (data.cell.text.includes('N/A')) {
          data.cell.styles.fontStyle = 'italic';
          data.cell.styles.textColor = [100, 100, 100];
        }
      },
    });

    // Access finalY from doc.lastAutoTable.finalY
    //console.log(doc.lastAutoTable.finalY)
    const sectionStartY = 482.4 + 20; // Start 30 points below the table
    const lineOffset = 3; // 3 points below the text baseline for the line
    const lineLength = 110;

    doc.setFontSize(10);

    // All text and lines will share these Y coordinates
    let textY = sectionStartY;
    const lineY = textY + lineOffset;

    // --- 'Tinanggap ni:' (Left-aligned) ---
    const receivedByText = 'Tinanggap ni:';
    const receivedByTextWidth = doc.getStringUnitWidth(receivedByText) * doc.internal.getFontSize();
    const receivedByLineStartX = 40 + receivedByTextWidth + 5; // Use 40pt margin for alignment
    doc.text(receivedByText, 40, textY);
    doc.line(receivedByLineStartX, lineY, receivedByLineStartX + lineLength, lineY);

    // --- 'Isinumite ni:' (Centered) ---
    const submittedByText = 'Isinumite ni:';
    const submittedByTextWidth = doc.getStringUnitWidth(submittedByText) * doc.internal.getFontSize();
    const pageCenterX = (doc.internal.pageSize.width / 2) - 40; 
    const submittedByTextStartX = pageCenterX - (submittedByTextWidth / 2);
    const submittedByLineStartX = submittedByTextStartX + submittedByTextWidth + 5;

    doc.text(submittedByText, pageCenterX, textY, { align: 'center' });
    doc.line(submittedByLineStartX, lineY, submittedByLineStartX + lineLength, lineY);


    // --- 'Binigyang Pansin:' (Right-aligned) ---
    const notedByText = 'Binigyang Pansin:';
    const lineTargetEndX = doc.internal.pageSize.width - 40; // Use 40pt margin for alignment
    const notedByLineStartX = lineTargetEndX - lineLength;
    const notedByTextEndX = notedByLineStartX - 5;

    doc.text(notedByText, notedByTextEndX, textY, { align: 'right' });
    doc.line(notedByLineStartX, lineY, lineTargetEndX, lineY);

    textY += 15;
    const kalihimX = receivedByLineStartX + (lineLength / 2); // Center below its line
    const pinunoX = submittedByLineStartX + (lineLength / 2); // Center below its line
    const kasapiX = notedByLineStartX + (lineLength / 2); // Center below its line


    doc.text("Mga Kasapi:", pageCenterX, lineY + 25, { align: 'center' });
    doc.line(receivedByLineStartX, lineY + 30, receivedByLineStartX + lineLength, lineY + 30);
    doc.line(submittedByLineStartX, lineY + 30, submittedByLineStartX + lineLength, lineY + 30);
    doc.line(submittedByLineStartX, lineY + 45, submittedByLineStartX + lineLength, lineY + 45);
    doc.line(submittedByLineStartX, lineY + 60, submittedByLineStartX + lineLength, lineY + 60);

    doc.setFontSize(8);
    doc.text('Kalihim', kalihimX, textY, { align: 'center' });
    doc.text('Pinuno ng Lupon/Core Group', pinunoX, textY, { align: 'center' });
    doc.text('Kasapi ng MTAC/TWG', kasapiX, textY, { align: 'center' });
    doc.text('Pinuno ng BDC/PPIC', receivedByLineStartX + 15, lineY + 45); // This line's x-coord may need adjustment if you want it exactly centered under the line
    doc.text('Punong Barangay', receivedByLineStartX + 18, lineY + 54); // This line's x-coord may need adjustment if you want it exactly centered under the line
    

    return doc;
  };


  const handleViewDownload = async (reportType, actionType) => { // Made async
    if (!isSearchApplied) {
      alert('Please click "Apply Search" first to enable actions.');
      return;
    }

    // For demonstration, externalData will be empty. In a real app, you might pass data here
    const externalDataForReport = []; 

    const doc = await generateReport(reportType, locationData, externalDataForReport); // Await the async function

    if (actionType === 'View') {
      doc.output("dataurlnewwindow");
    } else if (actionType === 'Download') {
      const filename = `${locationData.barangayName}_${locationData.municipalName}_${locationData.provinceName}`
      doc.save(`${reportType.replace(/\s+/g, '_')}_${filename.replace(/\s+/g, '_')}.pdf`);
    }
  };

  const bidpCategories = [
    'Sectoral Plan for Agriculture and Environment',
    'Sectoral Plan for Environmental Protection and Management',
    'Sectoral Plan for Health',
    'Sectoral Plan for Nutrition',
    'Sectoral Plan for Infrastructure', // Corrected typo
    'Sectoral Plan for Social Services',
    'Sectoral Plan for Youth and Sports Development',
  ];

  const disabledButtonStyles = {
    backgroundColor: '#cccccc',
    cursor: 'not-allowed',
    color: '#666666'
  };

  return (
    <div style={pageContainer}>
      {/* Removed local search input and apply search button */}
      <div style={reportContainer}>
        <h2 style={heading}>Barangay Integrated Development Plan (BIDP)</h2>
        <div style={reportCardsContainer}>
          {bidpCategories.map((category, index) => (
            <div key={index} style={card}>
              <h3>{category}</h3>
              <button
                onClick={() => handleViewDownload(category, 'View')}
                style={{
                  ...button,
                  marginRight: "10px",
                  ...(!isSearchApplied ? disabledButtonStyles : {})
                }}
                disabled={!isSearchApplied}
              >
                View
              </button>
              <button
                onClick={() => handleViewDownload(category, 'Download')}
                style={{
                  ...button,
                  ...(!isSearchApplied ? disabledButtonStyles : {})
                }}
                disabled={!isSearchApplied}
              >
                Download

              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Styling (remains the same)
const pageContainer = {
  marginTop: "80px",
  minHeight: "calc(100vh - 80px)",
  backgroundColor: "#f8f9fa",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%", // Ensure it takes full width of parent
};

const reportContainer = {
  width: "100%", // Adjusted to fill parent's width
  backgroundColor: "#ffffff",
  padding: "30px",
  borderRadius: "10px",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
};

const heading = {
  fontSize: "24px",
  fontWeight: "bold",
  marginBottom: "20px",
  textAlign: "center",
  color: "#333",
};

const reportCardsContainer = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  gap: "20px",
};

const card = {
  marginBottom: "20px",
  padding: "15px",
  border: "1px solid #ddd",
  borderRadius: "8px",
  backgroundColor: "#fefefe",
  boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
  flex: "1 1 calc(33% - 40px)", // Adjusted for 3 cards per row on larger screens
  minWidth: "280px",
  maxWidth: "calc(33% - 20px)",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
};

const button = {
  padding: "10px 20px",
  backgroundColor: "#007bff",
  color: "#fff",
  border: "none",
  borderRadius: "5px",
  fontWeight: "bold",
  cursor: "pointer",
  marginTop: "10px",
  transition: 'background-color 0.3s ease',
};

export default BIDP;
